<script setup>
/* global axios */
// O LIVRO DE TOQUES NA CONVERSA (fatia 1b, 1b.7).
//
// O que o advup mandou sozinho para esta pessoa (cadência, cobrança, lembretes, avisos,
// automações), o que o portão segurou e por quê, e quando sai o que está esperando a
// hora. Vem do servidor já sem texto de mensagem: o livro não guarda texto.
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { FUSO_ESCRITORIO } from 'dashboard/helper/fusoEscritorio';

const props = defineProps({
  conversationId: { type: Number, required: true },
});

const { t, tm, rt } = useI18n();
const accountId = useMapGetter('getCurrentAccountId');

const toques = ref([]);
const carregando = ref(false);
const erro = ref(false);

const carregar = async () => {
  const pedida = props.conversationId;
  carregando.value = true;
  erro.value = false;
  try {
    const { data } = await axios.get(
      `/api/v1/accounts/${accountId.value}/conversations/${pedida}/toques`
    );
    // A pessoa pode ter trocado de conversa enquanto o livro chegava.
    if (pedida !== props.conversationId) return;
    toques.value = data.payload || [];
  } catch {
    if (pedida === props.conversationId) erro.value = true;
  } finally {
    if (pedida === props.conversationId) carregando.value = false;
  }
};

watch(() => props.conversationId, carregar, { immediate: true });

// Nome desconhecido (origem nova antes de a tradução chegar) aparece cru, não some.
const nomeDe = (mensagens, chave) =>
  mensagens?.[chave] ? rt(mensagens[chave]) : chave;
const nomeDaOrigem = chave => nomeDe(tm('LIVRO_DE_TOQUES.ORIGEM'), chave);
const nomeDoEstado = chave => nomeDe(tm('LIVRO_DE_TOQUES.ESTADO'), chave);

// Três cores, não dez: saiu, segurou, deu problema.
const SAIU = ['solicitado', 'aceito', 'entregue', 'lido', 'reservado'];
const PROBLEMA = ['falhou', 'desconhecido'];
const tom = estado => {
  if (SAIU.includes(estado)) return 'saiu';
  if (PROBLEMA.includes(estado)) return 'problema';
  return 'segurou';
};

const data = iso =>
  iso
    ? new Date(iso).toLocaleString('pt-BR', {
        timeZone: FUSO_ESCRITORIO,
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';
</script>

<template>
  <section class="ldt" aria-live="polite">
    <p class="advup-rotulo advup-rotulo--secao">
      {{ t('LIVRO_DE_TOQUES.TITULO') }}
    </p>
    <p class="advup-nota ldt__explica">{{ t('LIVRO_DE_TOQUES.EXPLICACAO') }}</p>

    <p v-if="carregando && !toques.length" class="advup-nota">
      {{ t('LIVRO_DE_TOQUES.CARREGANDO') }}
    </p>
    <p v-else-if="erro" class="advup-nota advup-nota--erro">
      {{ t('LIVRO_DE_TOQUES.ERRO') }}
    </p>
    <p v-else-if="!toques.length" class="advup-nota ldt__vazio">
      {{ t('LIVRO_DE_TOQUES.VAZIO') }}
    </p>
    <ul v-else class="ldt__lista">
      <li
        v-for="(toque, i) in toques"
        :key="toque.id"
        class="ldt__item advup-entra"
        :style="{ '--i': Math.min(i, 10) }"
      >
        <div class="ldt__linha">
          <span
            class="ldt__estado"
            :class="`ldt__estado--${tom(toque.estado)}`"
          >
            {{ nomeDoEstado(toque.estado) }}
          </span>
          <span class="ldt__origem">{{ nomeDaOrigem(toque.origem) }}</span>
          <time class="ldt__quando">{{ data(toque.quando) }}</time>
        </div>
        <p v-if="toque.motivo" class="advup-nota ldt__motivo">
          {{ toque.motivo }}
        </p>
        <p v-if="toque.agendado_para" class="advup-nota ldt__sai-em">
          {{ t('LIVRO_DE_TOQUES.SAI_EM', { data: data(toque.agendado_para) }) }}
        </p>
      </li>
    </ul>
  </section>
</template>

<style scoped>
.ldt {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}
.ldt__lista {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin: 0;
  padding: 0;
  list-style: none;
}
.ldt__item {
  padding: 0.5rem 0.625rem;
  border: 1px solid rgb(var(--slate-6));
  border-radius: 0.5rem;
}
.ldt__linha {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.375rem 0.5rem;
  font-size: 0.8125rem;
}
.ldt__estado {
  padding: 0.0625rem 0.4375rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
}
.ldt__estado--saiu {
  color: rgb(var(--teal-11));
  background: rgb(var(--teal-3));
}
.ldt__estado--segurou {
  color: rgb(var(--amber-11));
  background: rgb(var(--amber-3));
}
.ldt__estado--problema {
  color: rgb(var(--ruby-11));
  background: rgb(var(--ruby-3));
}
.ldt__origem {
  color: rgb(var(--slate-12));
}
.ldt__quando {
  margin-left: auto;
  color: rgb(var(--slate-11));
  font-size: 0.75rem;
}
.ldt__motivo,
.ldt__sai-em {
  margin-top: 0.25rem;
}
</style>
